import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import RetentionKanbanOutcomeModal from './RetentionKanbanOutcomeModal';
import RenewalWindowCard from '../renewal-reminders/RenewalWindowCard';
import RenewalReminderOutcomeModal from '../renewal-reminders/RenewalReminderOutcomeModal';
import ContactLines from '../../components/ContactLines';

// ─── Retention Kanban (Sprint 21.2, 9/24/26) ─────────────────────────────────
// Per docs/COMS_Retention_Kanban_Design_Decisions_2026-09-24.md — same design
// language as PB101's redesign, mirroring PB101Kanban.jsx's structure. Reads
// GET /retention/kanban (getRetentionKanbanBoard, queries.js) and logs
// through the existing POST /retention/outcome route (logRetentionOutcome),
// extended this session with scriptKey / at_risk issueType / wantsToCancel.
// Renewal Window reuses the already-built /renewal-reminders data for a
// count + link-through, rather than re-implementing that stage's own
// confirm/outcome flow here — that flow has real, different mechanics
// (fd_confirmed_at gating, PbP membership ids) not worth duplicating.
// Does not touch RetentionCheckpoint.jsx/RetentionBacklog.jsx, which the
// existing /retention view keeps using unchanged.

const STAGE_META = {
  renewalWindow: { label: 'Renewal Window', owner: 'FD / Club', ownerColor: '#3b82f6',
                   note: 'Annual and extended-period renewals (45-day and 7-day touches), and prepaid promotions that end in 14 days. A promotion does not renew on its own: ask the member to sign up for a regular plan. With 5 days left it moves to Inside Sales (Escalated).' },
  paymentFailed: { label: 'Payment Failed', sla: '3-Day SLA', owner: 'FD / Club', ownerColor: '#3b82f6' },
  cancellation:  { label: 'Voluntary Cancellation', sla: '7-Day SLA', owner: 'Manager Only', ownerColor: '#f97316',
                   note: 'Members who cancelled in PlayByPoint and still have access until their paid period ends. Goal: a manager reaches out within 7 days of the cancellation (sooner is better); after that it goes to Inside Sales for a last attempt.' },
  atRisk:        { label: 'Disengaging / At-Risk', sla: '14+ days inactive', owner: 'FD / Club', ownerColor: '#3b82f6',
                   note: 'Active members with no visit in 14 days or more. Paused or cancelling members, new members (joined in the last 14 days), and open payment issues are not shown here.' },
  welld:         { label: 'Welld Engagement', sla: '14+ days inactive', owner: 'FD / Club', ownerColor: '#14b8a6',
                   note: 'Welld members with no visit in 14 days or more. Welld claims depend on visits, so every visit counts. Same rules as At Risk (paused, cancelling and new members are not shown).' },
  escalated:     { label: 'Escalated', owner: 'Inside Sales', ownerColor: '#a855f7',
                   note: 'Inside Sales\' last attempt: cancellations and failed payments the club could not save, and promotions ending in 5 days or less that the club has not converted to a regular plan.' },
  followUp:      { label: 'Follow Up Scheduled', owner: 'Whoever scheduled it', ownerColor: '#eab308',
                   note: 'At Risk, Welld and Escalated cards with a follow-up on the calendar. They stay here until the next contact is logged, and turn red once the date has passed. Payment Failed and Cancellation cards stay in their own columns because their deadlines run from the original date.' },
  saved:         { label: 'Saved', owner: 'Closed', ownerColor: '#22c55e' },
  lost:          { label: 'Lost', owner: 'Closed', ownerColor: '#6b7280' },
};

// At Risk first (BK, 10/5/26): it is the constant-contact, keep-them-in-programming stage; the
// stages after it are mostly the result of not doing that well. Escalated only follows a failed
// cancellation/payment save.
const COLUMN_ORDER = ['atRisk', 'welld', 'followUp', 'renewalWindow', 'paymentFailed', 'cancellation', 'escalated', 'saved', 'lost'];

function formatCountdown(msLeft) {
  if (msLeft === null || msLeft === undefined) return null;
  const overdue = msLeft <= 0;
  const abs = Math.abs(msLeft);
  const hours = Math.floor(abs / 3600000);
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  const label = days > 0 ? `${days}d ${remHours}h` : `${hours}h`;
  return { label: overdue ? `Overdue ${label}` : `${label} left`, overdue };
}

function countdownColor(msLeft, overdue) {
  if (overdue) return '#ef4444';
  const hoursLeft = msLeft / 3600000;
  if (hoursLeft <= 24) return '#ef4444';
  if (hoursLeft <= 48) return '#f97316';
  return '#22c55e';
}

function Badge({ children, color, title }) {
  return (
    <span title={title} style={{
      background: `${color}22`, color, border: `1px solid ${color}`,
      fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
      textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 4, display: 'inline-block', marginBottom: 4,
    }}>
      {children}
    </span>
  );
}

const FLAG_LABEL = { range: 'Range', people: 'People', rhythm: 'Rhythm' };
const FLAG_COLOR = { range: '#eab308', people: '#3b82f6', rhythm: '#a855f7' };

function CountdownBadge({ card }) {
  if (card.msLeft === undefined || card.msLeft === null) return null;
  const cd = formatCountdown(card.msLeft);
  const color = countdownColor(card.msLeft, cd.overdue);
  // Escalated = last shot with a 21-day auto-close (BK, 10/5/26); past it, the person still has
  // access (otherwise it would have closed), so say so plainly.
  if (card.stage === 'escalated') {
    const days = Math.max(0, Math.ceil(card.msLeft / 86400000));
    return cd.overdue
      ? <Badge color="#ef4444" title="Past the 21-day window but the member still has access, so it stays open">⏱ Past 21 days · still a member</Badge>
      : <Badge color={days <= 5 ? '#f97316' : '#a855f7'} title="Auto-closes to Win Back if no outcome is logged and the member no longer has access">⏳ Last chance · closes in {days}d</Badge>;
  }
  return <Badge color={color}>⏱ {cd.label}</Badge>;
}

function FollowUpBadge({ card }) {
  if (!card.followUpDate) return null;
  const today = new Date().toISOString().slice(0, 10);
  const color = card.followUpDate <= today ? '#ef4444' : '#3b82f6';
  const label = new Date(card.followUpDate + 'T12:00:00').toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
  return <Badge color={color}>📅 Follow up {label}</Badge>;
}

// ─── What a member pays (BK, 10/6/26) ─────────────────────────────────────────
// `card.billing` comes from PbP's last payment on the membership. It can be a discount/founder price
// and Welld/comp'd members show $0 or a sponsor payment, so cards say "last paid", not "plan price".
const money0 = cents => `$${Math.round(cents / 100).toLocaleString()}`;
const money2 = cents => `$${(cents / 100).toFixed(2)}`;
function billingLabel(card) {
  const b = card.billing;
  if (!b) return null;
  if (card.isWelld) return { text: 'Welld · sponsor-paid', color: '#14b8a6' };
  if (!b.amountCents) return { text: 'No payment on record (comp?)', color: '#6b7280' };
  if (b.cycle === 'monthly') return { text: `Paying ${money2(b.amountCents)}/mo`, color: '#22c55e' };
  if (b.cycle === 'annual') return { text: `${money0(b.amountCents)}/yr (about ${money0(b.monthlyCents)}/mo)`, color: '#22c55e' };
  return { text: `Last paid ${money2(b.amountCents)}${b.monthlyCents ? ` (about ${money0(b.monthlyCents)}/mo)` : ''}`, color: '#22c55e' };
}
// Monthly-equivalent revenue a column represents (Welld and comp'd excluded — they aren't member-paid).
function columnMonthlyRevenueCents(cards) {
  return (cards || []).reduce((sum, c) => (c.isWelld || !c.billing?.monthlyCents) ? sum : sum + c.billing.monthlyCents, 0);
}

// ─── Card (simplified 10/6/26: BK, "there is a lot going on here") ─────────────────────────────
// Name, phone/email, ONE plain-text status line, and at most ONE badge. Everything else (last visit,
// skill, program, script hints) lives in the log-outcome form, which opens when the card is clicked.
const ATTEMPT_OUTCOMES = ['No answer', 'Left voicemail', 'Still failing', 'Still deciding'];
const daysAgo = iso => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

// Where this card stands with contact: nothing logged, waiting on a reply, due another try, or reached.
// Same 5-day rule as the other boards.
function contactState(card) {
  if (!card.lastContactAt) return null;
  const d = daysAgo(card.lastContactAt);
  if (ATTEMPT_OUTCOMES.includes(card.lastOutcome)) {
    return d < 5
      ? { kind: 'waiting', color: '#f59e0b', label: `Awaiting response · ${d}d` }
      : { kind: 'retry', color: '#f97316', label: `Try again · last tried ${d}d ago` };
  }
  return { kind: 'reached', color: '#22c55e', label: `Reached ${new Date(card.lastContactAt).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}` };
}

function statusParts(card, stageKey) {
  const parts = [];
  const isEng = stageKey === 'atRisk' || stageKey === 'welld';
  if (isEng && card.lastVisitDate !== undefined) {
    const d = card.daysSinceLastVisit;
    parts.push({ text: d === null || d === undefined ? '180+ days inactive' : `${d} days inactive`,
      color: d === null || d === undefined || d >= 60 ? '#ef4444' : d >= 30 ? '#f97316' : '#f59e0b' });
  }
  if ((stageKey === 'cancellation' || stageKey === 'escalated') && card.accessEndsAt) {
    const d = Math.ceil((new Date(card.accessEndsAt).getTime() - Date.now()) / 86400000);
    parts.push(d > 0 ? { text: `Access ends in ${d}d`, color: d <= 3 ? '#ef4444' : d <= 7 ? '#f97316' : '#9ca3af' }
      : { text: 'Access ended', color: '#6b7280' });
  }
  if (card.isWelld && stageKey !== 'welld') parts.push({ text: 'Welld', color: '#14b8a6' });
  if (card.membershipPlan) {
    parts.push({ text: card.membershipPlan.length > 22 ? `${card.membershipPlan.slice(0, 21)}…` : card.membershipPlan, color: '#9ca3af' });
  }
  const bl = billingLabel(card);
  // Comped / no-payment members show nothing: the label added noise and the totals already skip them.
  if (bl && !card.isWelld && card.billing?.amountCents) {
    const b = card.billing;
    parts.push({ text: b.cycle === 'annual' ? `${money0(b.amountCents)}/yr` : b.cycle === 'monthly' ? `${money0(b.amountCents)}/mo` : money0(b.amountCents), color: '#6b9e78' });
  }
  if (isEng) (card.flags || []).forEach(f => parts.push({ text: FLAG_LABEL[f], color: FLAG_COLOR[f] }));
  return parts;
}

function KanbanCard({ card, stageKey, clubName, onLogOutcome, inFollowUpColumn }) {
  const isTerminal = stageKey === 'saved' || stageKey === 'lost';
  const state = !isTerminal ? contactState(card) : null;
  const parts = statusParts(card, stageKey);
  // At most one badge: the follow-up date in the follow-up column, else the deadline clock, else contact state.
  let badge = null;
  if (isTerminal) {
    badge = stageKey === 'saved' ? <Badge color="#22c55e">{card.outcomeType === 'saved' ? 'Saved' : 'Re-engaged'}</Badge>
      : <Badge color="#ef4444">Confirmed leaving</Badge>;
  } else if (inFollowUpColumn && card.followUpDate) {
    badge = <FollowUpBadge card={card} />;
  } else if (card.msLeft !== undefined && card.msLeft !== null) {
    badge = <CountdownBadge card={card} />;
  } else if (state) {
    badge = <Badge color={state.color}>{state.label}</Badge>;
  }
  // Escalated and cancellation cards also say what happened last, in the status line.
  const lastLine = !isTerminal && state && card.msLeft !== undefined && card.msLeft !== null
    ? `${card.lastOutcome || 'Contacted'} ${daysAgo(card.lastContactAt)}d ago` : null;
  return (
    <div
      onClick={!isTerminal ? () => onLogOutcome(card, card.originStage || stageKey) : undefined}
      title={!isTerminal ? 'Click to log an outcome' : undefined}
      style={{
        background: '#141414', border: '1px solid #2a2a2a', borderRadius: 7,
        padding: '7px 8px', marginBottom: 6,
        cursor: isTerminal ? 'default' : 'pointer',
      }}
    >
      <div style={{ fontWeight: 500, fontSize: 12, marginBottom: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {card.name}
      </div>
      <ContactLines phone={card.phone} email={card.email} name={card.name} size="xs" compact />
      {parts.length > 0 && (
        <div style={{ fontSize: 10, lineHeight: 1.45, marginBottom: badge || lastLine ? 4 : 0 }}>
          {parts.map((p, i) => (
            <span key={i}>{i > 0 && <span style={{ color: '#444' }}> · </span>}<span style={{ color: p.color }}>{p.text}</span></span>
          ))}
        </div>
      )}
      {lastLine && <div style={{ fontSize: 10, color: state.color, marginBottom: 4 }}>{lastLine}</div>}
      {badge && <div>{badge}</div>}
    </div>
  );
}

function Column({ stageKey, cards, totalCount, clubName, onLogOutcome, pctContacted, renewalSummary, renewalCards = [], promoHandoffCards = [], onRenewalConfirm, onRenewalOutcome, results, cancelMetrics }) {
  const meta = STAGE_META[stageKey];
  const isRenewal = stageKey === 'renewalWindow';
  // Promotions in their last 5 days are Inside Sales' last call (BK, 11/3/26: club first, then hand off).
  const promoCards = stageKey === 'escalated' ? promoHandoffCards : [];
  const count = isRenewal ? (renewalSummary?.total ?? 0) : cards.length + promoCards.length;
  const pct = pctContacted?.[stageKey];
  // Empty columns shrink to a thin strip so the work stands out (they reopen on their own when a card arrives).
  const collapsed = count === 0 && (totalCount === undefined || totalCount === 0);

  const revenueCents = ['atRisk', 'cancellation', 'escalated', 'paymentFailed'].includes(stageKey) ? columnMonthlyRevenueCents(cards) : 0;
  const revenueLabel = stageKey === 'atRisk' ? 'Paying' : stageKey === 'paymentFailed' ? 'Billing at stake' : 'Revenue leaving';
  const help = [meta.note,
    stageKey === 'cancellation' && cancelMetrics && cancelMetrics.opened > 0
      ? `Last 30 days: ${cancelMetrics.opened} cancelled, ${cancelMetrics.contacted48h} contacted within 48h (${Math.round((cancelMetrics.contacted48h / cancelMetrics.opened) * 100)}%), ${cancelMetrics.saved + cancelMetrics.reactivated} saved or kept, ${cancelMetrics.escalated} sent to Inside Sales.` : null,
    stageKey === 'atRisk' && results && results.contacted > 0
      ? `Results: of the people reached out to 14-44 days ago (At Risk + Welld), ${results.cameBack} of ${results.contacted} came back within 14 days (${results.pct}%).` : null,
    revenueCents ? `${revenueLabel}: monthly equivalent of each member's last payment (annual divided by 12). Excludes Welld and members with no payment on record.` : null,
  ].filter(Boolean).join('\n\n');

  return (
    <div style={{ flex: collapsed ? '0 0 96px' : '0 0 172px', width: collapsed ? 96 : 172 }}>
      <div style={{ background: '#1c1c1c', borderRadius: 7, padding: '5px 7px', marginBottom: 6 }}>
        <span style={{
          float: 'right', fontFamily: 'monospace', fontSize: 10, fontWeight: 500,
          background: '#0a0a0a', color: '#a8a496', padding: '1px 6px', borderRadius: 20,
        }}>{totalCount !== undefined && totalCount !== count ? `${count} / ${totalCount}` : count}</span>
        <div style={{ fontWeight: 700, fontSize: 10.5, color: '#a8a496', lineHeight: 1.3 }}>
          {COLUMN_ORDER.indexOf(stageKey) + 1} · {meta.label}{meta.sla && !collapsed ? ` · ${meta.sla}` : ''}
          {help && !collapsed && <span title={help} style={{ marginLeft: 5, color: '#60a5fa', cursor: 'help', fontWeight: 400 }}>ⓘ</span>}
        </div>
        {!collapsed && (
          <div style={{ marginTop: 3, display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: meta.ownerColor }}>
              {meta.owner}
            </span>
            {pct !== null && pct !== undefined && pct > 0 && <span style={{ fontSize: 9, color: '#666' }}>{pct}% contacted</span>}
          </div>
        )}
        {!collapsed && stageKey === 'cancellation' && (() => {
          const within30 = cards.filter(c => {
            if (!c.accessEndsAt) return false;
            const d = Math.ceil((new Date(c.accessEndsAt).getTime() - Date.now()) / 86400000);
            return d > 0 && d <= 30;
          }).length;
          return (
            <div style={{ fontSize: 9, color: '#f59e0b', marginTop: 3 }}
              title="PlayByPoint's own Upcoming Cancellations widget counts only memberships whose access ends within 30 days. This board lists every cancellation that still has access, including annual plans with months left.">
              {within30} end within 30 days
            </div>
          );
        })()}
        {!collapsed && revenueCents > 0 && (
          <div style={{ fontSize: 9, color: '#6b9e78', marginTop: 3 }}>{revenueLabel}: ~{money0(revenueCents)}/mo</div>
        )}
        {isRenewal && renewalSummary && (
          <div style={{ fontSize: 9, color: '#3b82f6', marginTop: 4, lineHeight: 1.4 }}>
            {renewalSummary.due7} 7-day · {renewalSummary.due45} 45-day{renewalSummary.due14 ? ` · ${renewalSummary.due14} promo ending` : ''} due
          </div>
        )}
      </div>

      {!collapsed && (isRenewal ? (
        <>
          {renewalCards.length === 0 && (
            <div style={{ fontSize: 10, color: '#555', textAlign: 'center', padding: '12px 4px', border: '1px dashed #2a2a2a', borderRadius: 7 }}>
              No renewal touches due
            </div>
          )}
          {renewalCards.map(r => (
            <RenewalWindowCard key={r.id} reminder={r} onConfirm={onRenewalConfirm} onLogOutcome={onRenewalOutcome} />
          ))}
        </>
      ) : (
        <>
          {cards.length === 0 && promoCards.length === 0 && (
            <div style={{ fontSize: 10, color: '#555', textAlign: 'center', padding: '12px 4px', border: '1px dashed #2a2a2a', borderRadius: 7 }}>
              Nothing here
            </div>
          )}
          {promoCards.map(r => (
            <RenewalWindowCard key={`promo_${r.id}`} reminder={r} onConfirm={onRenewalConfirm} onLogOutcome={onRenewalOutcome} />
          ))}
          {cards.map(c => (
            <KanbanCard key={c.id} card={c} stageKey={c.originStage || stageKey} clubName={clubName} onLogOutcome={onLogOutcome}
              inFollowUpColumn={stageKey === 'followUp'} />
          ))}
        </>
      ))}
    </div>
  );
}

// ─── At Risk filters (BK, 10/5/26) ────────────────────────────────────────────
// Client-side only: the At Risk column arrives complete (~150 cards per club), and
// every field these filter on (days inactive, plan, Welld, last contact, follow-up,
// flags) is already on the card. Filters touch only the At Risk column.
const AT_RISK_FILTER_KEY = 'retentionAtRiskFilters';
const DEFAULT_AT_RISK_FILTERS = {
  search: '', plan: 'all', minDays: '', maxDays: '',
  contact: 'all', flagRange: false, flagPeople: false, sort: 'recent',
};
const DAY_PRESETS = [
  { label: '14–30', min: 14, max: 30 },
  { label: '31–45', min: 31, max: 45 },
  { label: '46–90', min: 46, max: 90 },
  { label: '90+', min: 90, max: '' },
];

function loadAtRiskFilters() {
  try {
    const raw = window.localStorage.getItem(AT_RISK_FILTER_KEY);
    return raw ? { ...DEFAULT_AT_RISK_FILTERS, ...JSON.parse(raw) } : DEFAULT_AT_RISK_FILTERS;
  } catch { return DEFAULT_AT_RISK_FILTERS; }
}

function atRiskFilterCount(f) {
  return [f.search.trim(), f.plan !== 'all', f.minDays !== '' || f.maxDays !== '',
    f.contact !== 'all', f.flagRange, f.flagPeople].filter(Boolean).length; // sort is not a filter
}

function applyAtRiskFilters(cards, f) {
  const today = new Date().toISOString().slice(0, 10);
  const q = f.search.trim().toLowerCase();
  const min = f.minDays === '' ? null : Number(f.minDays);
  const max = f.maxDays === '' ? null : Number(f.maxDays);
  const filtered = cards.filter(c => {
    if (q && !`${c.name || ''} ${c.email || ''}`.toLowerCase().includes(q)) return false;
    if (f.plan !== 'all' && c.membershipPlan !== f.plan) return false;
    // null days = no visit on record in 180 days, i.e. beyond any max
    const days = c.daysSinceLastVisit === null || c.daysSinceLastVisit === undefined ? Infinity : c.daysSinceLastVisit;
    if (min !== null && days < min) return false;
    if (max !== null && days > max) return false;
    if (f.contact === 'not_contacted' && c.lastContactAt) return false;
    if (f.contact === 'contacted' && !c.lastContactAt) return false;
    if (f.contact === 'followup_due' && !(c.followUpDate && c.followUpDate <= today)) return false;
    const flags = c.flags || [];
    if (f.flagRange && !flags.includes('range')) return false;
    if (f.flagPeople && !flags.includes('people')) return false;
    return true;
  });
  // Most recently inactive first by default (BK, 10/5/26): work the list top-down; the
  // 100+ day / no-visit tail (often Welld, hardest to re-engage) ends up at the bottom.
  const d = c => (c.daysSinceLastVisit === null || c.daysSinceLastVisit === undefined ? Infinity : c.daysSinceLastVisit);
  const dir = f.sort === 'longest' ? -1 : 1;
  return [...filtered].sort((x, y) => {
    const dx = d(x), dy = d(y);
    if (dx === dy) return (x.name || '').localeCompare(y.name || '');
    if (dx === Infinity) return dir === 1 ? 1 : -1;
    if (dy === Infinity) return dir === 1 ? -1 : 1;
    return (dx - dy) * dir;
  });
}

// CSV of whatever the At Risk filters currently show — made for Marko's review
// (BK, 10/5/26): he marks mismatches in the two blank columns at the end.
function csvCell(v) {
  const t = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
}
function exportAtRiskCsv(cards, clubName) {
  const header = ['Name', 'Phone', 'Email', 'Days inactive', 'Last visit', 'Membership', 'Welld', 'Last contacted', 'Follow-up date', 'Flags', 'Belongs in At Risk? (Y/N)', 'Notes'];
  const lines = [header.map(csvCell).join(',')];
  for (const c of cards) {
    lines.push([
      c.name, c.phone, c.email,
      c.daysSinceLastVisit === null || c.daysSinceLastVisit === undefined ? '180+' : c.daysSinceLastVisit,
      c.lastVisitDate || '', c.membershipPlan || '', c.isWelld ? 'Yes' : 'No',
      c.lastContactAt ? String(c.lastContactAt).slice(0, 10) : '', c.followUpDate || '',
      (c.flags || []).join(' / '), '', '',
    ].map(csvCell).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `at-risk_${(clubName || 'club').replace(/\s+/g, '-').toLowerCase()}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}


function AtRiskFilterBar({ filters, setFilters, plans, shown, total, onExport }) {
  const set = patch => setFilters(prev => ({ ...prev, ...patch }));
  const active = atRiskFilterCount(filters);
  const lbl = { fontSize: 10, color: '#888', marginBottom: 2 };
  const field = { background: '#141414', border: '1px solid #2a2a2a', color: '#ddd', borderRadius: 6, fontSize: 11.5, padding: '4px 6px' };
  const chip = on => ({
    fontSize: 10.5, padding: '3px 8px', borderRadius: 20, cursor: 'pointer',
    border: `1px solid ${on ? '#f59e0b' : '#2a2a2a'}`, background: on ? '#f59e0b22' : '#141414', color: on ? '#f59e0b' : '#aaa',
  });
  const presetOn = p => String(filters.minDays) === String(p.min) && String(filters.maxDays) === String(p.max);
  return (
    <div style={{ background: '#101010', border: '1px solid #222', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: 11.5, fontWeight: 600, color: '#a8a496' }}>
          At Risk + Welld filters
          <span style={{ fontWeight: 400, color: '#888', marginLeft: 8 }}>
            showing {shown} of {total} (both columns){active ? ` · ${active} filter${active > 1 ? 's' : ''} on` : ''}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {active > 0 && (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 10.5, padding: '2px 8px' }}
              onClick={() => setFilters(DEFAULT_AT_RISK_FILTERS)}>Clear filters</button>
          )}
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 10.5, padding: '2px 8px' }}
            onClick={onExport}>⬇ Export CSV ({shown})</button>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <div>
          <div style={lbl}>Search</div>
          <input style={{ ...field, width: 140 }} placeholder="Name or email" value={filters.search}
            onChange={e => set({ search: e.target.value })} />
        </div>
        <div>
          <div style={lbl}>Membership</div>
          <select style={{ ...field, maxWidth: 190 }} value={filters.plan} onChange={e => set({ plan: e.target.value })}>
            <option value="all">All plans</option>
            {plans.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <div style={lbl}>Days inactive</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="number" min="14" style={{ ...field, width: 52 }} placeholder="min" value={filters.minDays}
              onChange={e => set({ minDays: e.target.value })} />
            <span style={{ color: '#666' }}>–</span>
            <input type="number" min="14" style={{ ...field, width: 52 }} placeholder="max" value={filters.maxDays}
              onChange={e => set({ maxDays: e.target.value })} />
          </div>
        </div>
        <div>
          <div style={lbl}>Quick ranges</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {DAY_PRESETS.map(p => (
              <span key={p.label} style={chip(presetOn(p))}
                onClick={() => set(presetOn(p) ? { minDays: '', maxDays: '' } : { minDays: String(p.min), maxDays: String(p.max) })}>
                {p.label}
              </span>
            ))}
          </div>
        </div>
        <div>
          <div style={lbl}>Contact</div>
          <select style={field} value={filters.contact} onChange={e => set({ contact: e.target.value })}>
            <option value="all">All</option>
            <option value="not_contacted">Not contacted yet</option>
            <option value="contacted">Already contacted</option>
            <option value="followup_due">Follow-up due</option>
          </select>
        </div>
        <div>
          <div style={lbl}>Sort</div>
          <select style={field} value={filters.sort} onChange={e => set({ sort: e.target.value })}>
            <option value="recent">Most recent first</option>
            <option value="longest">Longest inactive first</option>
          </select>
        </div>
        <div>
          <div style={lbl}>Flags</div>
          <div style={{ display: 'flex', gap: 4 }}>
            <span style={chip(filters.flagRange)} onClick={() => set({ flagRange: !filters.flagRange })}>Range</span>
            <span style={chip(filters.flagPeople)} onClick={() => set({ flagPeople: !filters.flagPeople })}>People</span>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Board-wide membership filter (BK, 10/6/26) ───────────────────────────────
// "Silence Welld but keep all other memberships on": check or uncheck any plan, with a one-click
// Welld toggle. Applies to every column on the board (renewal reminders have no plan on the card
// and are not filtered). Stored as the list of HIDDEN plans, so a new plan shows by default.
const HIDDEN_PLANS_KEY = 'retentionHiddenPlans';
const WELLD_KEY = '__welld__';
function loadHiddenPlans() {
  try { const v = JSON.parse(window.localStorage.getItem(HIDDEN_PLANS_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
}

function MembershipFilter({ plans, hidden, setHidden }) {
  const [open, setOpen] = useState(false);
  const isHidden = k => hidden.includes(k);
  const toggle = k => setHidden(isHidden(k) ? hidden.filter(x => x !== k) : [...hidden, k]);
  const hiddenCount = hidden.length;
  const row = { display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#ccc', padding: '3px 2px', cursor: 'pointer' };
  return (
    <div style={{ position: 'relative' }}>
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(o => !o)}
        style={{ fontSize: 12, borderColor: hiddenCount ? '#f59e0b' : undefined, color: hiddenCount ? '#f59e0b' : undefined }}>
        Memberships{hiddenCount ? ` · ${hiddenCount} hidden` : ' · all'} ▾
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 41, background: '#171717', border: '1px solid #333',
            borderRadius: 8, padding: 10, width: 270, maxHeight: 360, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setHidden([])}>Show all</button>
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => setHidden([WELLD_KEY])}>Hide Welld only</button>
            </div>
            <label style={row}>
              <input type="checkbox" checked={!isHidden(WELLD_KEY)} onChange={() => toggle(WELLD_KEY)} />
              <span style={{ color: '#14b8a6', fontWeight: 600 }}>Welld members (all Welld plans)</span>
            </label>
            <div style={{ borderTop: '1px solid #2a2a2a', margin: '6px 0' }} />
            {plans.map(pl => (
              <label key={pl} style={row}>
                <input type="checkbox" checked={!isHidden(pl)} onChange={() => toggle(pl)} />
                <span>{pl}</span>
              </label>
            ))}
            <div style={{ fontSize: 10, color: '#666', marginTop: 8, lineHeight: 1.4 }}>
              Checked = shown. Applies to every column. Saved on this computer.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function RetentionKanban() {
  const { get, post } = useApi();
  const { currentClub } = useClub();
  const [board, setBoard] = useState(null);
  const [renewalSummary, setRenewalSummary] = useState(null);
  const [renewalData, setRenewalData] = useState(null);
  const [renewalOutcomeTarget, setRenewalOutcomeTarget] = useState(null);
  const [boardSearch, setBoardSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [outcomeTarget, setOutcomeTarget] = useState(null); // { card, stageKey }
  const [atRiskFilters, setAtRiskFilters] = useState(loadAtRiskFilters);
  const [hiddenPlans, setHiddenPlans] = useState(loadHiddenPlans);
  const [filtersOpen, setFiltersOpen] = useState(false);
  useEffect(() => {
    try { window.localStorage.setItem(HIDDEN_PLANS_KEY, JSON.stringify(hiddenPlans)); } catch { /* per-viewer convenience only */ }
  }, [hiddenPlans]);

  useEffect(() => {
    try { window.localStorage.setItem(AT_RISK_FILTER_KEY, JSON.stringify(atRiskFilters)); } catch { /* per-viewer convenience only */ }
  }, [atRiskFilters]);

  const atRiskAll = board?.stages?.atRisk || [];
  const welldAll = board?.stages?.welld || [];
  const atRiskPlans = useMemo(
    () => [...new Set([...atRiskAll, ...welldAll].map(c => c.membershipPlan).filter(Boolean))].sort(),
    [atRiskAll, welldAll]
  );
  const atRiskShown = useMemo(() => applyAtRiskFilters(atRiskAll, atRiskFilters), [atRiskAll, atRiskFilters]);
  const welldShown = useMemo(() => applyAtRiskFilters(welldAll, atRiskFilters), [welldAll, atRiskFilters]);

  const load = useCallback(() => {
    if (!currentClub?.id) { setLoading(false); return; }
    setLoading(true);
    setLoadError('');
    Promise.all([
      get(`/retention/kanban?locationId=${currentClub.id}`),
      get(`/renewal-reminders?locationId=${currentClub.id}`).catch(() => null),
    ])
      .then(([kanban, renewal]) => {
        setBoard(kanban);
        if (renewal) {
          setRenewalData(renewal);
          setRenewalSummary({
            due7: (renewal.due7DayToday || []).length,
            due45: (renewal.due45DayToday || []).length,
            due14: (renewal.due14DayToday || []).length,
            total: (renewal.due7DayToday || []).length + (renewal.due45DayToday || []).length
              + (renewal.due14DayToday || []).length,
          });
        }
      })
      .catch(e => setLoadError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function handleRenewalConfirm(reminder) {
    try { await post(`/renewal-reminders/${reminder.id}/confirm`); load(); } catch (e) { console.error(e); }
  }

  function handleLogOutcome(card, stageKey) {
    setOutcomeTarget({ card, stageKey });
  }

  // Board-wide search (old Retention list searched by name/phone/email; the new board only had it
  // inside the At Risk filters) + a "contacted today" count like the old Daily List header.
  const q = boardSearch.trim().toLowerCase();
  const hit = c => !q || `${c.name || c.member_name || ''} ${c.email || ''} ${c.phone || ''}`.toLowerCase().includes(q);

  // Follow Up Scheduled (Josh/BK, 10/6/26): At Risk / Welld / Escalated cards that have a follow-up
  // scheduled leave their own column and sit here, soonest (or most overdue) first. Cards keep their
  // original stage (originStage) so the outcome form still shows that stage's script and outcomes.
  const FOLLOW_UP_FROM = ['atRisk', 'welld', 'escalated'];
  const planOk = c => !((c.isWelld && hiddenPlans.includes(WELLD_KEY)) || (c.membershipPlan && hiddenPlans.includes(c.membershipPlan)));
  const allBoardPlans = [...new Set(Object.values(board?.stages || {}).flat().map(c => c.membershipPlan).filter(Boolean))].sort();
  const baseCards = stageKey => (
    board.atRiskSource === 'snapshot' && stageKey === 'atRisk' ? atRiskShown
      : board.atRiskSource === 'snapshot' && stageKey === 'welld' ? welldShown
      : (board.stages?.[stageKey] || [])
  ).filter(hit).filter(planOk);
  const columnCards = stageKey => {
    if (stageKey === 'followUp') {
      return FOLLOW_UP_FROM
        .flatMap(k => baseCards(k).filter(c => c.followUpDate).map(c => ({ ...c, originStage: k })))
        .sort((x, y) => String(x.followUpDate).localeCompare(String(y.followUpDate)));
    }
    return FOLLOW_UP_FROM.includes(stageKey) ? baseCards(stageKey).filter(c => !c.followUpDate) : baseCards(stageKey);
  };
  const todayStr = new Date().toDateString();
  const contactedToday = board
    ? Object.values(board.stages || {}).flat().filter(c => c.lastContactAt && new Date(c.lastContactAt).toDateString() === todayStr).length
      + ((renewalData?.completedToday || []).length)
    : 0;
  const renewalCards = [...(renewalData?.due14DayToday || []), ...(renewalData?.due7DayToday || []), ...(renewalData?.due45DayToday || [])].filter(hit);
  const promoHandoffCards = (renewalData?.promoHandoff || []).filter(hit);

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (loadError) return (
    <div className="empty-state">
      <p>Couldn't load the retention pipeline: {loadError}</p>
    </div>
  );
  if (!board) return <div className="empty-state"><p>No data.</p></div>;

  const total = Object.values(board.counts || {}).reduce((a, b) => a + b, 0) + (renewalSummary?.total || 0) + promoHandoffCards.length;

  return (
    <div className="module-page kanban-page">
      <div className="module-header" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: 22 }}>🔄</span>
          <h1 className="module-title">Retention Pipeline</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <input
            value={boardSearch} onChange={e => setBoardSearch(e.target.value)}
            placeholder="Search the whole board: name, phone, email"
            style={{ background: '#141414', border: '1px solid #2a2a2a', color: '#ddd', borderRadius: 6, fontSize: 12, padding: '5px 9px', width: 250 }}
          />
          <MembershipFilter plans={allBoardPlans} hidden={hiddenPlans} setHidden={setHiddenPlans} />
          <div style={{ fontSize: 12.5, color: '#888' }}>
            {currentClub?.name || ''} · {total} active · <span style={{ color: '#22c55e' }}>{contactedToday} contacted today</span>
          </div>
        </div>
      </div>

      {board.atRiskSource === 'snapshot' && (atRiskAll.length + welldAll.length) > 0 && (
        filtersOpen ? (
          <>
            <AtRiskFilterBar filters={atRiskFilters} setFilters={setAtRiskFilters}
              plans={atRiskPlans} shown={atRiskShown.length + welldShown.length} total={atRiskAll.length + welldAll.length}
              onExport={() => exportAtRiskCsv([...atRiskShown, ...welldShown], currentClub?.name)} />
            <div style={{ textAlign: 'right', marginTop: -6, marginBottom: 8 }}>
              <span onClick={() => setFiltersOpen(false)} style={{ fontSize: 11, color: '#60a5fa', cursor: 'pointer' }}>Hide filters ▴</span>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, fontSize: 12, color: '#888' }}>
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }} onClick={() => setFiltersOpen(true)}>
              At Risk filters{atRiskFilterCount(atRiskFilters) > 0 ? ` · ${atRiskFilterCount(atRiskFilters)} on` : ''} ▾
            </button>
            <span>showing {atRiskShown.length + welldShown.length} of {atRiskAll.length + welldAll.length} At Risk + Welld</span>
            <span style={{ marginLeft: 'auto' }}>
              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                onClick={() => exportAtRiskCsv([...atRiskShown, ...welldShown], currentClub?.name)}>⬇ Export CSV ({atRiskShown.length + welldShown.length})</button>
            </span>
          </div>
        )
      )}

      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, alignItems: 'flex-start' }}>
        {COLUMN_ORDER.filter(k => !(k === 'welld' && hiddenPlans.includes(WELLD_KEY))).map(stageKey => (
          <Column
            key={stageKey}
            stageKey={stageKey}
            cards={columnCards(stageKey)}
            renewalCards={renewalCards}
            promoHandoffCards={promoHandoffCards}
            onRenewalConfirm={handleRenewalConfirm}
            onRenewalOutcome={setRenewalOutcomeTarget}
            totalCount={board.atRiskSource === 'snapshot' && stageKey === 'atRisk' ? atRiskAll.length
              : board.atRiskSource === 'snapshot' && stageKey === 'welld' ? welldAll.length : undefined}
            results={board.engagementResults}
            cancelMetrics={board.cancellationMetrics}
            clubName={currentClub?.name}
            onLogOutcome={handleLogOutcome}
            pctContacted={board.pctContacted}
            renewalSummary={renewalSummary}
          />
        ))}
      </div>

      {renewalOutcomeTarget && (
        <RenewalReminderOutcomeModal
          reminder={renewalOutcomeTarget}
          onClose={() => setRenewalOutcomeTarget(null)}
          onSaved={() => { setRenewalOutcomeTarget(null); load(); }}
        />
      )}

      {outcomeTarget && (
        <RetentionKanbanOutcomeModal
          card={outcomeTarget.card}
          stageKey={outcomeTarget.stageKey}
          clubName={currentClub?.name}
          onClose={() => setOutcomeTarget(null)}
          onSaved={() => { setOutcomeTarget(null); load(); }}
        />
      )}
    </div>
  );
}
